import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { navigationLinks } from '../mock/data';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubDropdown, setOpenSubDropdown] = useState(null);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [isMobileLoginDropdownOpen, setIsMobileLoginDropdownOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setOpenSubDropdown(null);
    setIsMobileMenuOpen(false);
    setIsLoginDropdownOpen(false);
    setIsMobileLoginDropdownOpen(false);
  }, [location.pathname]);

  const isRouteHref = (href) => href.startsWith('/');
  const isExternalHref = (href) => /^https?:\/\//i.test(href);
  const isSectionHref = (href) => href.startsWith('#');

  const handleLinkClick = (e, href) => {
    if (isExternalHref(href)) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
      setOpenDropdown(null);
      setOpenSubDropdown(null);
      return;
    }

    if (!isSectionHref(href)) {
      return;
    }

    if (!isHomePage) {
      // If not on homepage, navigate to homepage first
      window.location.href = '/' + href;
      return;
    }
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-2xl shadow-purple-500/10'
          : 'bg-white/80 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - High Resolution Image */}
          <Link to="/">
            <motion.div
              className="flex items-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_boxes-new/artifacts/cbaiea2t_TWUKLogo.png"
                alt="TransactWorld Logo"
                className="h-8 w-auto sm:h-10"
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigationLinks.map((link, idx) => (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                {link.dropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button className="text-gray-700 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-cyan-600 hover:to-purple-600 font-semibold transition-all duration-300 flex items-center space-x-1">
                      <span>{link.name}</span>
                      <motion.div
                        animate={{ rotate: openDropdown === link.name ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {openDropdown === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-100 py-2"
                        >
                          {link.dropdown.map((item, subIdx) => (
                            <div
                              key={item.name}
                              className="relative"
                              onMouseEnter={() => item.children && setOpenSubDropdown(item.name)}
                              onMouseLeave={() => item.children && setOpenSubDropdown(null)}
                            >
                              {item.children ? (
                                <button
                                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 font-medium transition-all duration-200"
                                >
                                  <span>{item.name}</span>
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              ) : isRouteHref(item.href) ? (
                                <Link
                                  to={item.href}
                                  className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 font-medium transition-all duration-200"
                                >
                                  {item.name}
                                </Link>
                              ) : (
                                <motion.a
                                  href={item.href}
                                  onClick={(e) => handleLinkClick(e, item.href)}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: subIdx * 0.05 }}
                                  whileHover={{ x: 5, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                                  className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 font-medium transition-all duration-200"
                                >
                                  {item.name}
                                </motion.a>
                              )}

                              <AnimatePresence>
                                {item.children && openSubDropdown === item.name && (
                                  <motion.div
                                    initial={{ opacity: 0, x: -10, scale: 0.95 }}
                                    animate={{ opacity: 1, x: 0, scale: 1 }}
                                    exit={{ opacity: 0, x: -10, scale: 0.95 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute top-0 left-full ml-2 w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-100 py-2 z-50"
                                  >
                                    {item.children.map((child) => (
                                      <Link
                                        key={child.name}
                                        to={child.href}
                                        className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 font-medium transition-all duration-200"
                                      >
                                        {child.name}
                                      </Link>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.a
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.href)}
                    whileHover={{ scale: 1.05 }}
                    className="text-gray-700 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-cyan-600 hover:to-purple-600 font-semibold transition-all duration-300"
                  >
                    {link.name}
                  </motion.a>
                )}
              </motion.div>
            ))}
          </div>

          {/* CTA Buttons with gradient */}
          <div className="hidden lg:flex items-center space-x-4">
            <div
              className="relative"
              onMouseEnter={() => setIsLoginDropdownOpen(true)}
              onMouseLeave={() => setIsLoginDropdownOpen(false)}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-gray-700 hover:text-purple-600 font-semibold transition-colors duration-200 flex items-center space-x-1"
              >
                <span>Login</span>
                <motion.div
                  animate={{ rotate: isLoginDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {isLoginDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.97 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-100 py-2 z-50"
                  >
                    <a
                      href="https://secure.transactworld.com/merchant/index.jsp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 font-medium transition-all duration-200"
                    >
                      Gateway Login
                    </a>
                    <a
                      href="https://account.transactworld.com/login"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 font-medium transition-all duration-200"
                    >
                      IBAN Login
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <motion.a
              href="https://account.payswix.com/register?ref=A00031"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(139, 92, 246, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="relative px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-purple-500/30 overflow-hidden group"
            >
              <span className="relative z-10">Sign Up</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              ></motion.div>
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="lg:hidden text-gray-700 hover:text-purple-600 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden py-4 border-t border-purple-100"
            >
              {navigationLinks.map((link) => (
                <div key={link.name} className="py-2">
                  {link.dropdown ? (
                    <div>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === link.name ? null : link.name)}
                        className="w-full text-left text-gray-700 hover:text-purple-600 font-semibold py-2 flex items-center justify-between"
                      >
                        <span>{link.name}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            openDropdown === link.name ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openDropdown === link.name && (
                        <div className="pl-4 py-2 space-y-2">
                          {link.dropdown.map((item) => (
                            <div key={item.name}>
                              {item.children ? (
                                <button
                                  onClick={() => setOpenSubDropdown(openSubDropdown === item.name ? null : item.name)}
                                  className="w-full text-left text-gray-600 hover:text-purple-600 py-1 flex items-center justify-between"
                                >
                                  <span>{item.name}</span>
                                  <ChevronDown
                                    className={`w-4 h-4 transition-transform ${
                                      openSubDropdown === item.name ? 'rotate-180' : ''
                                    }`}
                                  />
                                </button>
                              ) : isRouteHref(item.href) ? (
                                <Link
                                  to={item.href}
                                  className="block text-gray-600 hover:text-purple-600 py-1"
                                >
                                  {item.name}
                                </Link>
                              ) : (
                                <a
                                  href={item.href}
                                  onClick={(e) => handleLinkClick(e, item.href)}
                                  className="block text-gray-600 hover:text-purple-600 py-1"
                                >
                                  {item.name}
                                </a>
                              )}

                              {item.children && openSubDropdown === item.name && (
                                <div className="pl-4 pt-1 pb-2 space-y-1">
                                  {item.children.map((child) => (
                                    <Link
                                      key={child.name}
                                      to={child.href}
                                      className="block text-gray-600 hover:text-purple-600 py-1 text-sm"
                                    >
                                      {child.name}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => handleLinkClick(e, link.href)}
                      className="block text-gray-700 hover:text-purple-600 font-semibold py-2"
                    >
                      {link.name}
                    </a>
                  )}
                </div>
              ))}
              <div className="pt-4 space-y-2">
                <button
                  onClick={() => setIsMobileLoginDropdownOpen(!isMobileLoginDropdownOpen)}
                  className="w-full text-center text-gray-700 hover:text-purple-600 font-semibold py-2 border border-gray-300 rounded-xl flex items-center justify-center space-x-2"
                >
                  <span>Login</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isMobileLoginDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isMobileLoginDropdownOpen && (
                  <div className="pl-2 space-y-1">
                    <a
                      href="https://secure.transactworld.com/merchant/index.jsp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-gray-600 hover:text-purple-600 py-1.5"
                    >
                      Gateway Login
                    </a>
                    <a
                      href="https://account.transactworld.com/login"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-gray-600 hover:text-purple-600 py-1.5"
                    >
                      IBAN Login
                    </a>
                  </div>
                )}
                <a
                  href="https://account.payswix.com/register?ref=A00031"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-gradient-to-r from-cyan-500 to-purple-600 text-white py-2 rounded-xl font-semibold"
                >
                  Sign Up
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;